import React, { useEffect, useState } from 'react';
import { X, Gift, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../axiosInstance';
import { useApp } from '../../contexts/AppContext';
import { toast } from '../toast';
import InlinePaymentPicker from './InlinePaymentPicker';

const CallGiftPicker = ({ isOpen, onClose, callId, recipient }) => {
  const { t } = useTranslation('livestream');
  const { walletBalance, updateBalance } = useApp();
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [payment, setPayment] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    axiosInstance.get('/gifts', { params: { creator_id: recipient?.id } })
      .then(({ data }) => {
        if (cancelled) return;
        const list = data?.data?.gifts || data?.gifts || data?.data || [];
        setGifts(Array.isArray(list) ? list : []);
      })
      .catch(() => { if (!cancelled) setGifts([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, recipient?.id]);

  // Reset state when closing
  useEffect(() => {
    if (!isOpen) { setSelected(null); setSending(false); }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSend = async () => {
    if (!selected) return;
    if (!payment?.gateway) {
      toast.error(t('videoCall.common.pickPaymentMethod'));
      return;
    }
    const price = Number(selected.price);
    if (payment.gateway === 'wallet' && walletBalance < price) {
      toast.error(t('videoCall.common.insufficientBalance'));
      return;
    }

    setSending(true);
    try {
      const res = await axiosInstance.post(`/calls/${callId}/gift`, {
        gift_id: selected.id,
        gateway: payment.gateway,
        payment_method_id: payment.paymentMethodId,
      });
      if (res.data.success) {
        if (payment.gateway === 'wallet') updateBalance(-price);
        toast.success(t('videoCall.giftPicker.sentGift', { name: selected.name }));
        onClose();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || t('videoCall.giftPicker.giftFailed'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[300] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white dark:bg-neutral-900 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl flex flex-col max-h-[90vh]">
        <div
          className="relative px-5 py-4 text-white flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, var(--color-primary-500), var(--color-primary-700))' }}
        >
          <button onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/15">
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Gift className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs opacity-80">{t('videoCall.giftPicker.title')}</p>
              <p className="font-semibold truncate">{recipient?.display_name || recipient?.username}</p>
            </div>
          </div>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          {loading ? (
            <div className="py-10 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-primary-500)' }} />
            </div>
          ) : gifts.length === 0 ? (
            <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-8">
              {t('videoCall.giftPicker.noGifts')}
            </p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {gifts.map((g) => {
                const isSel = selected?.id === g.id;
                return (
                  <button
                    key={g.id}
                    onClick={() => setSelected(g)}
                    className={`flex flex-col items-center p-3 rounded-xl border-2 transition-all ${
                      isSel
                        ? 'shadow-md'
                        : 'border-gray-100 dark:border-neutral-700 hover:bg-gray-50 dark:hover:bg-neutral-800 border-transparent'
                    }`}
                    style={isSel ? {
                      borderColor: 'var(--color-primary-500)',
                      backgroundColor: 'color-mix(in srgb, var(--color-primary-500) 8%, transparent)',
                    } : {}}
                  >
                    <div className="h-10 flex items-center justify-center mb-1">
                      {g.image_url ? (
                        <img src={g.image_url} alt={g.name} className="w-10 h-10 object-contain" />
                      ) : (
                        <span className="text-3xl">{g.emoji || '🎁'}</span>
                      )}
                    </div>
                    <p className="text-[11px] font-medium text-gray-800 dark:text-gray-200 truncate w-full">{g.name}</p>
                    <p className="text-[11px] font-bold" style={{ color: 'var(--color-primary-600)' }}>
                      ${Number(g.price).toFixed(2)}
                    </p>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Payment picker + Send — only shown once a gift is selected */}
        {selected && (
          <div className="border-t border-gray-100 dark:border-neutral-800 p-4 space-y-3 flex-shrink-0">
            <div>
              <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1.5">{t('videoCall.common.payWith')}</label>
              <InlinePaymentPicker value={payment} onChange={setPayment} />
            </div>
            <button
              onClick={handleSend}
              disabled={sending || !payment?.gateway}
              className="w-full py-3 rounded-full text-white font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, var(--color-primary-500), var(--color-primary-600))' }}
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
              {t('videoCall.giftPicker.sendButton', { name: selected.name, price: Number(selected.price).toFixed(2) })}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CallGiftPicker;
